import { FC } from "react";
import Image from "next/image";

import { ExtendedViewItem, Receipt } from "@/types/receipt";

import { translate } from "@/utils/formatters";
import {
  aggregateArticles,
  categorizeServices,
  getActionText,
  getStoreName,
} from "@/utils/extendedViewHelpers";

import { Button } from "@/components/common";
import { Send } from "@/components/icons";
import { useExtendedServiceHandler } from "@/hooks/useExtendedServiceHandler";
import { useActiveAccountingWidget } from "@/hooks/useActiveAccountingWidget";
import { hasEmailExportFor } from "@/config/emailExportWidgets";
import { EmailExportSection } from "./EmailExportSection";
import { SharingWarning } from "./SharingWarning";

import styles from "./ServiceViewPopup.module.css";
import { useExtendedWidgetVisibility } from "@/hooks/useExtendedWidgetVisibility";

interface ServiceViewPopupProps {
  extendedViewItems: ExtendedViewItem[];
  extendedViewImages: { [key: string]: string };
  lang: string;
  user_id?: string;
  receipt_id?: string;
  receipt: Receipt;
  email?: string | null;
  phoneNumber?: string | null;
  closeButton?: React.ReactNode;
}

export const ServiceViewPopup: FC<ServiceViewPopupProps> = ({
  extendedViewItems,
  extendedViewImages,
  lang,
  user_id,
  receipt_id,
  receipt,
  email,
  phoneNumber,
  closeButton,
}) => {
  const { handleServiceClick, isLoading } = useExtendedServiceHandler({
    receipt,
    lang,
    user_id,
    receipt_id,
    email,
    phoneNumber,
  });
  const { getStoredEmail, onAccountingSendSuccess } =
    useActiveAccountingWidget();
  const storeName = getStoreName(receipt);

  const { serviceWidgets } = useExtendedWidgetVisibility({
    extended_view_items: extendedViewItems,
    extended_view_images: extendedViewImages,
    currentView: "service_view",
  });

  const { receiptServices, merchantOffers } =
    categorizeServices(serviceWidgets);

  const handleButtonClick = (item: ExtendedViewItem, itemKey: string) => {
    const articles =
      item.extended_view_position === "website"
        ? aggregateArticles(receipt)
        : null;

    handleServiceClick(item, itemKey, { articles });
  };

  const renderServiceSection = (items: ExtendedViewItem[], title: string) => {
    if (items.length === 0) return null;

    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h5 className={styles.sectionTitle}>{title}</h5>
        
        </div>

        {items.map((item) => {
          const position = item.extended_view_position;
          const itemKey = `${position}_${item.originalIndex}`;
          const hasEmailExport = hasEmailExportFor(
            user_id,
            receipt_id,
            item.receipt_view_service_settings_id
          );

          return (
            <div key={itemKey} className={styles.itemCard}>
              <div className={styles.itemHeader}>
                <div className={styles.itemTitle}>{item.title}</div>
                {item.imageSrc && (
                  <Image
                    src={item.imageSrc}
                    alt={item.title}
                    width={32}
                    height={32}
                    className={styles.itemLogo}
                  />
                )}
              </div>

              <div className={styles.itemDescription}>{item.description}</div>

              {user_id && receipt_id && (
                <EmailExportSection
                  userId={user_id}
                  receiptId={receipt_id}
                  lang={lang}
                  settingsId={item.receipt_view_service_settings_id}
                  storeName={storeName}
                  initialEmail={getStoredEmail(
                    item.receipt_view_service_settings_id
                  )}
                  onSendSuccess={onAccountingSendSuccess(item)}
                />
              )}

              <SharingWarning
                item={item}
                email={email}
                phoneNumber={phoneNumber}
                lang={lang}
              />

              {!hasEmailExport && (
                <Button
                  className={styles.actionButton}
                  onClick={() => handleButtonClick(item, itemKey)}
                  text={getActionText(position, lang, {
                    service_view_button_text: item.service_view_button_text,
                  })}
                  isLoading={isLoading(itemKey)}
                  icon={<Send />}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="box head">
        <h4 className={styles.title}>
          {translate(lang, "Kvitteringsservices", "Receipt Services")}
        </h4>
        {closeButton}
      </div>

      <div className={`box ${styles.contentBox}`}>
        {renderServiceSection(
          merchantOffers,
          translate(lang, "BUTIKKTILBUD", "MERCHANT SERVICES")
        )}

        {renderServiceSection(
          receiptServices,
          translate(lang, "KVITTERINGSTJENESTER", "PARTNER SERVICES")
        )}
      </div>
    </>
  );
};
